/**
 * /tenants/$slug/directory/scim-operations — route shim.
 *
 * Validates URL search params via Zod, prefetches the first page
 * via `ensureQueryData`, then mounts the SCIM operations page.
 *
 * Permission gating happens inside the page component itself
 * (operators without scim.read see a "Forbidden" surface). The
 * route loader still runs the API call — the backend's
 * PermSCIMRead gate returns 403 which the API client maps to
 * ForbiddenError, which the loader propagates. That gives
 * tenant admins without permission a clean error boundary
 * rather than a half-rendered table.
 */

import { createFileRoute } from "@tanstack/react-router";
import { fetchScimOperations } from "@/features/directory/queries";
import {
  scimOpsListSearchSchema,
  SCIM_OPS_LIST_LIMIT,
  type ScimOpsListSearch,
} from "@/features/directory/scim-ops-search";
import { ScimOperationsPage } from "@/features/directory/pages/scim-operations-page";

export const Route = createFileRoute(
  "/_authed/tenants/$slug/directory/scim-operations",
)({
  validateSearch: (raw: Record<string, unknown>): ScimOpsListSearch =>
    scimOpsListSearchSchema.parse(raw),
  loaderDeps: ({ search }) => ({ offset: search.offset ?? 0 }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData({
      queryKey: [
        "scim",
        "operations-list",
        context.tenant.id,
        SCIM_OPS_LIST_LIMIT,
        deps.offset,
      ],
      queryFn: () =>
        fetchScimOperations(context.tenant.id, {
          limit: SCIM_OPS_LIST_LIMIT,
          offset: deps.offset,
        }),
    }),
  component: ScimOperationsPage,
});
